import React, { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import cn from 'classnames';
import AlertType from './AlertType';
import { defaultIconMap } from './constants';
import styles from './alerts.module.scss';
import { IconComponentType } from '@creditflowresearch/uilib/Icon';
import IconClose from '@creditflowresearch/uilib/Icon/IconClose';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import IconButton from '@creditflowresearch/uilib/IconButton';

export interface IAlertAction {
  /** Button label text */
  label: string,
  /** Button click handler */
  onClick: () => void | Promise<void>,
  /** Whether to hide alert after click. Default: true */
  hideOnClick?: boolean,
}

export interface IAlertProps {
  /** Alert type determining color and default icon */
  type?: AlertType,
  /** Title displayed above message */
  title?: React.ReactNode,
  /** Alert message content */
  message: React.ReactNode | string,
  /** Auto-hide timeout in ms. 0 = no auto-hide */
  timeOut?: number,
  /** Click handler for the entire alert body */
  onClick?: () => void,
  /** Called when alert is hidden (by timeout or close) */
  onHide?: () => void,
  /** true = default per type, false/undefined = hidden, component = custom */
  icon?: IconComponentType | boolean,
  /** Shows close (✕) button */
  closable?: boolean,
  /** Called when close button is clicked */
  onClose?: () => void,
  /** Action button configuration */
  action?: IAlertAction,
  /** Secondary action button, rendered after `action` */
  secondaryAction?: IAlertAction,
  /** Shows loading spinner */
  isLoading?: boolean,
  /** Additional CSS class */
  className?: string,
  /** Triggers bounce animation (deduplication feedback) */
  bounce?: boolean,
}

const Alert = forwardRef<HTMLDivElement, IAlertProps>(({
  type = AlertType.INFO,
  message = null,
  timeOut = 5000,
  title = null,
  icon,
  className,
  closable = false,
  onClose,
  action,
  secondaryAction,
  isLoading = false,
  onClick,
  onHide,
  bounce = false,
}: IAlertProps, ref) => {
  const [loading, setLoading] = useState(isLoading);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef<number>(timeOut);
  const startTimeRef = useRef<number>(0);

  const handleHide = useCallback(() => {
    if (onHide) {
      onHide();
    }
  }, [onHide]);

  const startTimer = useCallback(() => {
    if (remainingRef.current <= 0 || timeOut === 0) return;
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(handleHide, remainingRef.current);
  }, [timeOut, handleHide]);

  const pauseTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      remainingRef.current -= Date.now() - startTimeRef.current;
    }
  }, []);

  useEffect(() => {
    if (timeOut !== 0 && !closable) {
      remainingRef.current = timeOut;
      startTimer();
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [timeOut, startTimer]);

  const handleMouseEnter = () => {
    pauseTimer();
  };

  const handleMouseLeave = () => {
    startTimer();
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
      if (!closable) {
        handleHide();
      }
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClose) {
      onClose();
    }
    handleHide();
  };

  const runAction = (actionToRun: IAlertAction) => (e: React.MouseEvent) => {
    e.stopPropagation();
    const shouldHide = actionToRun.hideOnClick !== false;
    const result = actionToRun.onClick();
    if (result instanceof Promise) {
      setLoading(true);
      result
        .then(() => shouldHide && handleHide())
        .finally(() => setLoading(false));
    } else {
      shouldHide && handleHide();
    }
  };

  const resolveIcon = (): IconComponentType | null => {
    if (icon === true) return defaultIconMap[type];
    if (icon === false || icon === undefined) return null;
    return icon;
  };

  const Icon = resolveIcon();
  const hasExtras = !!Icon || !!action || !!secondaryAction || closable || loading;

  const alertCls = cn(
    styles.alert,
    className,
    {
      [styles.info]: type === AlertType.INFO,
      [styles.success]: type === AlertType.SUCCESS,
      [styles.warning]: type === AlertType.WARNING,
      [styles.error]: type === AlertType.ERROR,
      [styles.hasExtras]: hasExtras,
      [styles.bounce]: bounce,
    },
  );

  if (typeof message === 'string' || message instanceof String) {
    message = <span dangerouslySetInnerHTML={ { __html: message } } />;
  }

  return (
    <div
      ref={ ref }
      className={ styles.wrp }
      onMouseEnter={ handleMouseEnter }
      onMouseLeave={ handleMouseLeave }
    >
      <div className={ alertCls } onClick={ handleClick }>
        <div className={ styles.body } role="alert">
          { !loading && Icon && (
            <Icon className={ styles.icon } />
          ) }
          { loading && (
            <Spinner size="S" color="on-color" inline className={ styles.spinner } />
          ) }
          <div className={ styles.content }>
            { title && (
              <h4 className={ styles.title }>{ title }</h4>
            ) }
            <span className={ styles.message }>{ message }</span>
          </div>
        </div>
        { action && (
          <button
            type="button"
            data-test="alertActionBtn"
            className={ styles.actionBtn }
            onClick={ runAction(action) }
          >
            { action.label }
          </button>
        ) }
        { secondaryAction && (
          <button
            type="button"
            data-test="alertSecondaryActionBtn"
            className={ styles.actionBtn }
            onClick={ runAction(secondaryAction) }
          >
            { secondaryAction.label }
          </button>
        ) }
        { closable && (
          <IconButton
            icon={ <IconClose className={ styles.closeIcon } /> }
            dataTest="alertCloseBtn"
            className={ styles.closeBtn }
            onClick={ handleClose }
            ariaLabel="Close alert"
          />
        ) }
      </div>
    </div>
  );
});

export default Alert;
